import React, { useState, useEffect } from 'react';
import {
  Box,
  Heading,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  IconButton,
  Badge,
  useToast,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  ModalCloseButton,
  useDisclosure,
  HStack,
  Spinner,
  Text,
  Button,
  Code,
} from '@chakra-ui/react';
import { FiEye, FiTrash2 } from 'react-icons/fi';
import { tenantHeaders } from '../tenant';

const API_BASE = '/api/v1';

function Webhooks() {
  const toast = useToast();
  const [webhooks, setWebhooks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [viewingWebhook, setViewingWebhook] = useState(null);
  
  const { isOpen: isViewOpen, onOpen: onViewOpen, onClose: onViewClose } = useDisclosure();
  const { isOpen: isDeleteOpen, onOpen: onDeleteOpen, onClose: onDeleteClose } = useDisclosure();
  const [webhookToDelete, setWebhookToDelete] = useState(null);

  const getAuthHeaders = () => ({
    'Content-Type': 'application/json',
    'Authorization': `Token ${localStorage.getItem('token')}`,
    ...tenantHeaders(),
  });

  const fetchWebhooks = () => {
    fetch(`${API_BASE}/webhooks/`, { headers: getAuthHeaders() })
      .then(res => res.json())
      .then(data => {
        setWebhooks(Array.isArray(data) ? data : []);
        setLoading(false);
      })
      .catch(() => setLoading(false));
  };

  useEffect(() => {
    fetchWebhooks();
  }, []);

  const openViewWebhook = (webhook) => {
    fetch(`${API_BASE}/webhooks/${webhook.id}/`, { headers: getAuthHeaders() })
      .then(res => res.json())
      .then(data => {
        setViewingWebhook(data);
        onViewOpen();
      });
  };

  const confirmDelete = (webhook) => {
    setWebhookToDelete(webhook);
    onDeleteOpen();
  };

  const handleDelete = async () => {
    if (!webhookToDelete) return;
    try {
      const res = await fetch(`${API_BASE}/webhooks/${webhookToDelete.id}/`, {
        method: 'DELETE',
        headers: getAuthHeaders(),
      });
      if (res.ok) {
        fetchWebhooks();
        toast({ title: 'Webhook deleted', status: 'success', duration: 3000 });
      } else {
        toast({ title: 'Error deleting webhook', status: 'error', duration: 3000 });
      }
    } catch (e) {
      toast({ title: 'Error deleting webhook', status: 'error', duration: 3000 });
    }
    onDeleteClose();
    setWebhookToDelete(null);
  };

  if (loading) {
    return <Box textAlign="center" py={20}><Spinner size="xl" color="orange.500" /></Box>;
  }

  return (
    <Box>
      <Heading size="lg" mb={6}>Webhooks</Heading>

      <Box bg="white" p={6} borderRadius="lg" boxShadow="md" overflowX="auto">
        <Table variant="simple">
          <Thead>
            <Tr>
              <Th>Event</Th>
              <Th>Created</Th>
              <Th>Actions</Th>
            </Tr>
          </Thead>
          <Tbody>
            {webhooks.map(webhook => (
              <Tr key={webhook.id}>
                <Td>
                  <Badge colorScheme="orange">{webhook.event || 'Unknown'}</Badge>
                </Td>
                <Td>{new Date(webhook.created_at).toLocaleString()}</Td>
                <Td>
                  <HStack spacing={2}>
                    <IconButton
                      icon={<FiEye />}
                      size="sm"
                      variant="ghost"
                      onClick={() => openViewWebhook(webhook)}
                      aria-label="View"
                    />
                    <IconButton
                      icon={<FiTrash2 />}
                      size="sm"
                      variant="ghost"
                      colorScheme="red"
                      onClick={() => confirmDelete(webhook)}
                      aria-label="Delete"
                    />
                  </HStack>
                </Td>
              </Tr>
            ))}
          </Tbody>
        </Table>
        {webhooks.length === 0 && (
          <Text color="gray.500" textAlign="center" py={8}>No webhooks found</Text>
        )}
      </Box>

      {/* View Modal */}
      <Modal isOpen={isViewOpen} onClose={onViewClose} size="xl">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Webhook Details</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            {viewingWebhook && (
              <>
                <Text><strong>Event:</strong> {viewingWebhook.event}</Text>
                <Text><strong>Created:</strong> {new Date(viewingWebhook.created_at).toLocaleString()}</Text>
                <Text mt={4}><strong>Event Data:</strong></Text>
                <Box bg="gray.100" p={4} borderRadius="md" mt={2} maxH="400px" overflowY="auto">
                  <Code display="block" whiteSpace="pre-wrap" bg="transparent">
                    {JSON.stringify(JSON.parse(viewingWebhook.event_data || '{}'), null, 2)}
                  </Code>
                </Box>
              </>
            )}
          </ModalBody>
          <ModalFooter>
            <Button onClick={onViewClose}>Close</Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* Delete Confirmation Modal */}
      <Modal isOpen={isDeleteOpen} onClose={onDeleteClose}>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Confirm Delete</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            Are you sure you want to delete this webhook?
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={onDeleteClose}>Cancel</Button>
            <Button colorScheme="red" onClick={handleDelete}>Delete</Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Box>
  );
}

export default Webhooks;
